import React, {useState} from 'react';
import {createPortal} from 'react-dom';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';

import Button from './Button';
import Icon from './Icon';
import Overlay from './Overlay';
import * as api from '../lib/api';
import {
  BrowserPrintError,
  getPrinters,
  printZpl,
  getRememberedPrinterUid,
  rememberPrinterUid
} from '../lib/zebraPrint';

/**
 * "Print to Zebra" action for a single data row. Renders the label server-side (via the API, identical
 * to the scale hot-path) and sends the ZPL to a printer through the local Zebra Browser Print app.
 *
 * When more than one printer is available a small chooser is shown (pre-selecting the last-used or
 * default printer); with exactly one printer it prints straight away. The chooser is portalled to the
 * document body so it sits above the row-detail side modal it is triggered from.
 */
function PrintToZebraControl({sheetId, rowId, credentials}) {
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(null); // {devices, selectedUid} while the chooser is open

  const handleClick = async () => {
    setBusy(true);
    try {
      // Resolve the printer first: the label is rendered server-side scaled to the selected printer's
      // DPI (parsed from its Browser Print name), so we need the target before asking for the ZPL.
      const {devices, defaultUid} = await getPrinters();

      if (devices.length === 0) {
        NotificationManager.error(
          'No printers are set up in Browser Print on this computer. Add your printer in the Browser '
          + 'Print app, then try again.', 'No printers found');
        return;
      }

      if (devices.length === 1) {
        await renderAndSend(devices[0]);
        return;
      }

      setPicker({devices, selectedUid: preferredPrinterUid(devices, defaultUid)});
    }
    catch (err) {
      NotificationManager.error(describeError(err), 'Could not print');
    }
    finally {
      setBusy(false);
    }
  };

  const handleConfirmPrint = async () => {
    const device = picker.devices.find(d => d.uid === picker.selectedUid) || picker.devices[0];
    setPicker(null);
    await renderAndSend(device);
  };

  // Render the label scaled to this printer's DPI, then send it. deviceLabel(device) is the Browser
  // Print name the backend parses the DPI token from; an unrecognised name leaves the 203dpi default.
  const renderAndSend = async (device) => {
    setBusy(true);
    try {
      const {zpl} = await api.renderRowLabel(sheetId, rowId, credentials, deviceLabel(device));
      await printZpl(device, zpl);
      rememberPrinterUid(device.uid);
      NotificationManager.success(`Label sent to ${deviceLabel(device)}.`, 'Printed');
    }
    catch (err) {
      NotificationManager.error(describeError(err), 'Could not print');
    }
    finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button secondary type="button" disabled={busy} onClick={handleClick}>
        <Icon name="printer" /> {busy ? 'Printing...' : 'Print to Zebra'}
      </Button>

      {picker && createPortal(
        <>
          <Overlay visible style={{zIndex: 40}} onClick={() => setPicker(null)} />
          <PickerCard role="dialog" aria-label="Choose a printer">
            <h4>Choose a printer</h4>
            <PrinterSelect
              value={picker.selectedUid || ''}
              onChange={e => {
                const selectedUid = e.target.value;
                setPicker(current => ({...current, selectedUid}));
              }}>
              {picker.devices.map(device => (
                <option key={device.uid} value={device.uid}>{deviceLabel(device)}</option>
              ))}
            </PrinterSelect>
            <PickerButtons>
              <Button type="button" onClick={handleConfirmPrint}>Print</Button>
              <Button secondary type="button" onClick={() => setPicker(null)}>Cancel</Button>
            </PickerButtons>
          </PickerCard>
        </>,
        document.body
      )}
    </>
  );
}

// Prefer the printer the user last printed to, then Browser Print's default, then the first available.
function preferredPrinterUid(devices, defaultUid) {
  const remembered = getRememberedPrinterUid();
  if (remembered && devices.some(d => d.uid === remembered)) {
    return remembered;
  }
  if (defaultUid && devices.some(d => d.uid === defaultUid)) {
    return defaultUid;
  }
  return devices[0].uid;
}

function deviceLabel(device) {
  return device.name || device.uid || 'Printer';
}

function describeError(err) {
  if (err instanceof BrowserPrintError) {
    return err.message;
  }
  // The api client throws the raw fetch Response on a non-2xx status.
  if (err && typeof err.status === 'number') {
    switch (err.status) {
      case 400:
        return 'This row can\'t be printed. Its form needs a label layout and a weight (scale) question.';
      case 403:
        return 'You don\'t have permission to print labels for this form.';
      case 404:
        return 'This row could not be found.';
      default:
        return 'The server could not render the label. Please try again.';
    }
  }
  return 'Something went wrong while printing. Please try again.';
}

const PickerCard = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 41;
  width: 24rem;
  max-width: calc(100vw - 2rem);
  background: #ffffff;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 0.5rem 2rem rgba(4, 4, 15, 0.25);

  h4 {
    margin: 0 0 1rem 0;
    font-size: 1.125rem;
  }
`;

const PrinterSelect = styled.select`
  width: 100%;
  padding: 0.5rem;
  font-size: 1rem;
  border: 0.0625rem solid #d4d4d4;
  border-radius: 0.25rem;
  background: #ffffff;
`;

const PickerButtons = styled.div`
  margin-top: 1.5rem;
  display: flex;
  gap: 0.75rem;
`;

export default PrintToZebraControl;
